/** Phone Number class.
 * E.164
 * Internationally recognized standard for telephone numbers anywhere in the World called E.164. The format is broken down as follows:
 *
 * A telephone number can have a maximum of 15 digits
 * The first part of the telephone number is the country code
 * The second part is the national destination code (NDC)
 * The last part is the subscriber number (SN)
 * The NDC and SN together are collectively called the national (significant) number.
 */
export class PhoneNumber {
  private readonly IRELAND = '+353';

  private default = this.IRELAND;

  private number: string;

  /**
   * Stores the Phone Number representation.
   * @param phoneNumber
   */
  constructor(phoneNumber: string) {
    this.number = this.getPhoneNumber(phoneNumber);
  }

  /** Format phone number as E.164 @see {@link https://en.wikipedia.org/wiki/E.164} */
  public get e164(): string {
    return this.number;
  }

  private get defaultCountry(): string {
    return this.IRELAND;
  }

  /**
   * Map string to phone number.
   * @param value Mobile Phone number to be cleaned up.
   * @returns String without spaces and `-` characters.
   */
  public static cleanUpNumber(value: string): string {
    return value.replace(/[\(\)\s\-]/g, '');
  }

  /**
   * According to the official ITU E.164 recommendation {@link https://www.itu.int/rec/T-REC-E.164/en} the format must be a number up to fifteen digits in length starting with a ‘+’.
   * @param value Value to be checked is it valid E.164 format.
   * @returns `true` when the value is valid E.164 format.
   */
  public static isE164Format(value: string): boolean {
    const pattern = /^\+[1-9]\d{1,14}$/;

    return pattern.test(value);
  }

  private getPhoneNumber(phoneNumber: string): string {
    const startsWithPlus = /^\+/;
    if (startsWithPlus.test(phoneNumber)) {
      return phoneNumber;
    }

    let newSubscriberNumber = phoneNumber;
    const startsWithZero = /^0/;
    if (startsWithZero.test(phoneNumber)) {
      newSubscriberNumber = phoneNumber.replace(/^0/, '');
    }

    return `${this.defaultCountry}${newSubscriberNumber}`;
  }
}
